/* eslint-disable react-hooks/exhaustive-deps */
import { useQuery } from "@tanstack/react-query";
import { instance } from "libs/api";
import { SocketsContext } from "libs/soket";
import { ModeratorRoutesPrivileges } from "../../../constants";
import { useHasPrivilege } from "../../useHasPrivilege";
import {
  MutableRefObject,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

interface InAppConversationLite {
  id: string;
  unreadCount: number | null;
}

interface InAppMessageLite {
  conversationId: string;
  direction: "inbound" | "outbound";
}

interface ListResponseLite {
  data: InAppConversationLite[];
}

const SUPPORT_INITIAL_QUERY_KEY = [
  "support",
  "sidebar-counter-initial-list"
] as const;

const SUPPORT_TAKE = 50;

interface ContextValue {
  supportConversationsCount: number;
  decrementSupportUnread: (count: number) => void;
  openConversationIdRef: MutableRefObject<string | null>;
  setOpenSupportConversation: (id: string | null) => void;
}

const SupportUnreadContext = createContext<ContextValue>({
  supportConversationsCount: 0,
  decrementSupportUnread: () => {},
  openConversationIdRef: { current: null },
  setOpenSupportConversation: () => {}
});

export const SupportUnreadProvider = ({ children }: { children: ReactNode }) => {
  const { inAppSupportAdminSocket } = useContext(SocketsContext);

  const canAccess = useHasPrivilege(ModeratorRoutesPrivileges.AiAssistants);

  const { data } = useQuery<ListResponseLite>({
    queryKey: SUPPORT_INITIAL_QUERY_KEY,
    queryFn: async () =>
      await instance.get("admin/in-app-support/conversations", {
        params: { page: 1, take: SUPPORT_TAKE }
      }),
    enabled: canAccess,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  const initialUnread =
    data?.data.reduce((sum, c) => sum + (c.unreadCount ?? 0), 0) ?? 0;

  const [delta, setDelta] = useState(0);
  const openConversationIdRef = useRef<string | null>(null);

  useEffect(() => {
    const socket = inAppSupportAdminSocket.socket;
    if (!socket) return;

    const onMessage = (m: InAppMessageLite) => {
      if (m.direction !== "inbound") return;
      // skip if this very chat is currently open on screen
      if (m.conversationId === openConversationIdRef.current) return;
      setDelta((d) => d + 1);
    };

    socket.on("in-app-message.created", onMessage);
    return () => {
      socket.off("in-app-message.created", onMessage);
    };
  }, [inAppSupportAdminSocket.socket]);

  const decrementSupportUnread = useCallback((count: number) => {
    if (count <= 0) return;
    setDelta((d) => d - count);
  }, []);

  const setOpenSupportConversation = useCallback((id: string | null) => {
    openConversationIdRef.current = id;
  }, []);

  const value = useMemo<ContextValue>(
    () => ({
      supportConversationsCount: Math.max(0, initialUnread + delta),
      decrementSupportUnread,
      openConversationIdRef,
      setOpenSupportConversation
    }),
    [initialUnread, delta, decrementSupportUnread, setOpenSupportConversation]
  );

  return (
    <SupportUnreadContext.Provider value={value}>
      {children}
    </SupportUnreadContext.Provider>
  );
};

export const useSupportConversationsCounter = () =>
  useContext(SupportUnreadContext);
